import {
  hexToDarkerHslChannels,
  hexToHslChannels,
} from "@/lib/inspection-theme-color";
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

type InspectionThemeContextValue = {
  themeColor: string | null;
  setThemeColor: (color: string | null) => void;
};

const InspectionThemeContext =
  createContext<InspectionThemeContextValue | null>(null);

/** CSS custom properties overridden when a jurisdiction theme_color is active. */
const THEME_STYLE_KEYS = [
  "--primary",
  "--primary-foreground",
  "--accent",
  "--ring",
  "--foreground",
  "--card-foreground",
  "--popover-foreground",
  "--secondary-foreground",
  "--sidebar-foreground",
  "--sidebar-primary",
  "--sidebar-accent-foreground",
  "--sidebar-ring",
  "--color-primary",
  "--color-primary-foreground",
  "--color-accent",
  "--color-ring",
  "--color-foreground",
  "--color-card-foreground",
  "--color-popover-foreground",
  "--color-secondary-foreground",
  "--color-sidebar-foreground",
  "--color-sidebar-primary",
  "--color-sidebar-accent-foreground",
  "--color-sidebar-ring",
] as const;

function clearThemeFromPortalRoot(root: HTMLElement) {
  for (const key of THEME_STYLE_KEYS) {
    root.style.removeProperty(key);
  }
  root.removeAttribute("data-jurisdiction-theme");
}

/**
 * Apply jurisdiction theme_color to every portal token that normally uses the
 * default dark green (primary, foreground chrome, rings, accents, sidebar).
 */
function applyThemeToPortalRoot(color: string | null) {
  const root = document.querySelector(".inspection-portal");
  if (!(root instanceof HTMLElement)) return;

  if (!color) {
    clearThemeFromPortalRoot(root);
    return;
  }

  const channels = hexToHslChannels(color);
  const accent = hexToDarkerHslChannels(color);
  if (!channels) return;

  root.style.setProperty("--primary", channels);
  root.style.setProperty("--primary-foreground", "0 0% 100%");
  root.style.setProperty("--ring", channels);
  // Replace green-derived text/chrome tokens so themed pages are comprehensive.
  root.style.setProperty("--foreground", channels);
  root.style.setProperty("--card-foreground", channels);
  root.style.setProperty("--popover-foreground", channels);
  root.style.setProperty("--secondary-foreground", channels);
  root.style.setProperty("--sidebar-foreground", channels);
  root.style.setProperty("--sidebar-primary", channels);
  root.style.setProperty("--sidebar-accent-foreground", channels);
  root.style.setProperty("--sidebar-ring", channels);
  if (accent) root.style.setProperty("--accent", accent);

  root.style.setProperty("--color-primary", `hsl(${channels})`);
  root.style.setProperty("--color-primary-foreground", "hsl(0 0% 100%)");
  root.style.setProperty("--color-ring", `hsl(${channels})`);
  root.style.setProperty("--color-foreground", `hsl(${channels})`);
  root.style.setProperty("--color-card-foreground", `hsl(${channels})`);
  root.style.setProperty("--color-popover-foreground", `hsl(${channels})`);
  root.style.setProperty("--color-secondary-foreground", `hsl(${channels})`);
  root.style.setProperty("--color-sidebar-foreground", `hsl(${channels})`);
  root.style.setProperty("--color-sidebar-primary", `hsl(${channels})`);
  root.style.setProperty(
    "--color-sidebar-accent-foreground",
    `hsl(${channels})`,
  );
  root.style.setProperty("--color-sidebar-ring", `hsl(${channels})`);
  if (accent) root.style.setProperty("--color-accent", `hsl(${accent})`);

  root.setAttribute("data-jurisdiction-theme", color);
}

export function InspectionThemeProvider({ children }: { children: ReactNode }) {
  const [themeColor, setThemeColorState] = useState<string | null>(null);

  const setThemeColor = useCallback((color: string | null) => {
    setThemeColorState(color);
  }, []);

  useEffect(() => {
    applyThemeToPortalRoot(themeColor);
    return () => applyThemeToPortalRoot(null);
  }, [themeColor]);

  const value = useMemo(
    () => ({ themeColor, setThemeColor }),
    [themeColor, setThemeColor],
  );

  return (
    <InspectionThemeContext.Provider value={value}>
      {children}
    </InspectionThemeContext.Provider>
  );
}

/** Read the current inspection portal theme (null outside the provider). */
export function useInspectionTheme(): InspectionThemeContextValue | null {
  return useContext(InspectionThemeContext);
}

export function useInspectionThemeColor(color: string | null | undefined) {
  const ctx = useContext(InspectionThemeContext);
  useEffect(() => {
    if (!ctx) return;
    ctx.setThemeColor(color ?? null);
    const root = document.querySelector(".inspection-portal");
    if (root instanceof HTMLElement) {
      root.setAttribute("data-inspection-property", "");
    }
    return () => {
      ctx.setThemeColor(null);
      if (root instanceof HTMLElement) {
        root.removeAttribute("data-inspection-property");
      }
    };
  }, [ctx, color]);
}
